import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AddFileTeamMemberDto,
  CreateLgContactDto,
  LgContactsSummary,
  AppointableCandidate,
  AppointmentSummary,
  CompanyMemberSummary,
  CompanySummary,
  ContractSummary,
  CreateAppointmentDto,
  CreateCompanyMemberDto,
  CreateContractDto,
  CreateGovernmentMemberDto,
  FileTeamMember,
  GovernmentMemberSummary,
  GrantSigningAuthorityDto,
  RevocationPlan,
  SessionAccess,
  SignContractDto,
  SigningAuthorityRow,
  SigningCandidate,
  UpdateContractDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';

/**
 * The Government Portal's administration surface.
 *
 * A request that *creates* something at a municipality names it —
 * `localGovernmentId` in the body or query — because there is no record yet to
 * read the scope from. A request about an existing contract or appointment does
 * not need to: the API scopes it to the municipality the record belongs to, and
 * ignores any id the client sends. Where a typed body would reject an unknown
 * field (signing, terminating, revoking), it is not sent at all.
 */

const STALE_MS = 30 * 1000;

export const adminKeys = {
  session: (scopeKind?: string, scopeId?: string) =>
    ['gov-admin', 'session', scopeKind ?? '', scopeId ?? ''] as const,
  companies: ['gov-admin', 'companies'] as const,
  company: (id: string) => ['gov-admin', 'companies', id] as const,
  companyMembers: (id: string) => ['gov-admin', 'companies', id, 'members'] as const,
  contracts: (filter: string) => ['gov-admin', 'contracts', filter] as const,
  appointments: (lg: string) => ['gov-admin', 'appointments', lg] as const,
  appointable: (lg: string) => ['gov-admin', 'appointable', lg] as const,
  members: (lg: string) => ['gov-admin', 'members', lg] as const,
  signing: (lg: string) => ['gov-admin', 'signing', lg] as const,
  fileTeam: (fileId: string) => ['gov-admin', 'file-team', fileId] as const,
};

// ── Session ────────────────────────────────────────────────────────────────

/**
 * Where the account can work, and what it may do in the active scope.
 *
 * Drives navigation and button visibility only. The API re-checks every call, so
 * a stale or tampered response costs the user a dead link, never access.
 */
/**
 * What the signed-in account may do, at the selected scope.
 *
 * `userId` gates and keys the query: before sign-in there is nobody to ask
 * about (the probe only 401s and caches the failure), and after a sign-in —
 * including as somebody else on the same tab — the answer must be fetched for
 * that account rather than served from the previous one's cache.
 */
export function useSessionAccess(scopeKind?: string, scopeId?: string, userId?: string | null) {
  return useQuery({
    queryKey: [...adminKeys.session(scopeKind, scopeId), userId ?? ''],
    enabled: Boolean(userId),
    queryFn: async () => {
      const params = scopeKind && scopeId ? { scopeKind, scopeId } : undefined;
      const { data } = await api.get<SessionAccess>('/administration/session', { params });
      return data;
    },
    staleTime: STALE_MS,
  });
}

// ── Companies ──────────────────────────────────────────────────────────────

export function useCompanies(enabled = true) {
  return useQuery({
    queryKey: adminKeys.companies,
    queryFn: async () => {
      const { data } = await api.get<CompanySummary[]>('/administration/companies');
      return data;
    },
    enabled,
    staleTime: STALE_MS,
  });
}

export function useCompanyMembers(companyId: string | null | undefined) {
  return useQuery({
    queryKey: adminKeys.companyMembers(companyId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<CompanyMemberSummary[]>(
        `/administration/companies/${companyId}/members`,
      );
      return data;
    },
    enabled: Boolean(companyId),
  });
}

export function useAddCompanyMember(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateCompanyMemberDto) => {
      const { data } = await api.post<CompanyMemberSummary>(
        `/administration/companies/${companyId}/members`,
        dto,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.companyMembers(companyId) }),
  });
}

export function useUpdateCompanyMember(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...dto }: { id: string; role?: string; isActive?: boolean }) => {
      const { data } = await api.patch<CompanyMemberSummary>(
        `/administration/companies/${companyId}/members/${id}`,
        dto,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.companyMembers(companyId) }),
  });
}

// ── Contracts ──────────────────────────────────────────────────────────────

export function useContracts(filter: { localGovernmentId?: string; companyId?: string }) {
  const key = `${filter.localGovernmentId ?? ''}:${filter.companyId ?? ''}`;
  return useQuery({
    queryKey: adminKeys.contracts(key),
    queryFn: async () => {
      const { data } = await api.get<ContractSummary[]>('/administration/contracts', {
        params: filter,
      });
      return data;
    },
    staleTime: STALE_MS,
  });
}

function useContractMutation<TBody>(
  path: (id: string) => string,
  method: 'post' | 'patch' = 'post',
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: TBody }) => {
      const { data } = await api[method]<ContractSummary>(path(id), body);
      return data;
    },
    // Broad rather than surgical: signing a contract changes what the whole
    // portal can reach, so the session and every scoped list behind it are
    // stale, not just the contract row.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gov-admin'] }),
  });
}

export function useCreateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateContractDto) => {
      const { data } = await api.post<ContractSummary>('/administration/contracts', dto);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gov-admin'] }),
  });
}

/**
 * One contract, for the detail and edit pages.
 *
 * The municipality rides in the query string because the API resolves the
 * permission scope from it — see the route's own note on why fetching by id
 * alone would be a cross-tenant read.
 */
export function useContract(id: string | null | undefined, localGovernmentId: string | undefined) {
  return useQuery({
    queryKey: ['gov-admin', 'contract', id ?? ''],
    queryFn: async () => {
      const { data } = await api.get<ContractSummary>(`/administration/contracts/${id}`, {
        params: { localGovernmentId },
      });
      return data;
    },
    enabled: Boolean(id && localGovernmentId),
  });
}

export const useUpdateContract = () =>
  useContractMutation<UpdateContractDto>((id) => `/administration/contracts/${id}`, 'patch');

/**
 * File the executed contract PDF.
 *
 * The scope rides in the query string, not the form. Nest runs guards before
 * interceptors, so on a multipart request the body is still empty when the
 * permission guard reads it — a `localGovernmentId` form field would resolve to
 * nothing and the upload would be refused for everybody.
 */
export function useUploadContractDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      localGovernmentId,
      file,
    }: {
      id: string;
      localGovernmentId: string;
      file: File;
    }) => {
      const form = new FormData();
      form.append('document', file);
      const { data } = await api.post<ContractSummary>(
        `/administration/contracts/${id}/document`,
        form,
        { params: { localGovernmentId } },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gov-admin'] }),
  });
}

export function useDeleteContractDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, localGovernmentId }: { id: string; localGovernmentId: string }) => {
      const { data } = await api.delete<ContractSummary>(
        `/administration/contracts/${id}/document`,
        { params: { localGovernmentId } },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gov-admin'] }),
  });
}

/**
 * Open the executed PDF in a new tab.
 *
 * Two hops rather than one link: the object sits in a private bucket, so the API
 * mints a short-lived signed URL and the browser follows that. A new tab rather
 * than a navigation, so an officer mid-way through the administration screen
 * does not lose it to a download.
 */
export async function openContractDocument(id: string, localGovernmentId: string): Promise<void> {
  const { data } = await api.get<{ url: string; filename: string }>(
    `/administration/contracts/${id}/document`,
    { params: { localGovernmentId } },
  );
  window.open(data.url, '_blank', 'noopener,noreferrer');
}

export const useSubmitContract = () =>
  useContractMutation<{ localGovernmentId: string }>(
    (id) => `/administration/contracts/${id}/submit`,
  );

export const useSignContract = () =>
  useContractMutation<SignContractDto>((id) => `/administration/contracts/${id}/sign`);

export const useRejectContract = () =>
  useContractMutation<{ reason: string }>((id) => `/administration/contracts/${id}/reject`);

export const useSuspendContract = () =>
  useContractMutation<{ localGovernmentId: string }>(
    (id) => `/administration/contracts/${id}/suspend`,
  );

export const useResumeContract = () =>
  useContractMutation<{ localGovernmentId: string }>(
    (id) => `/administration/contracts/${id}/resume`,
  );

/**
 * What terminating a contract would do — fetched before the confirm dialog, so
 * the council member sees how many live files change hands before they act.
 */
export function useTerminationPlan(contractId: string | null, localGovernmentId: string) {
  return useQuery({
    queryKey: ['gov-admin', 'termination-plan', contractId ?? ''],
    queryFn: async () => {
      const { data } = await api.get<RevocationPlan>(
        `/administration/contracts/${contractId}/termination-plan`,
        { params: { localGovernmentId } },
      );
      return data;
    },
    enabled: Boolean(contractId),
  });
}

export const useTerminateContract = () =>
  useContractMutation<{ reason?: string }>((id) => `/administration/contracts/${id}/terminate`);

// ── Appointments ───────────────────────────────────────────────────────────

export function useAppointments(localGovernmentId: string | null | undefined) {
  return useQuery({
    queryKey: adminKeys.appointments(localGovernmentId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<AppointmentSummary[]>('/administration/appointments', {
        params: { localGovernmentId },
      });
      return data;
    },
    enabled: Boolean(localGovernmentId),
  });
}

export function useAppointableUsers(localGovernmentId: string | null | undefined) {
  return useQuery({
    queryKey: adminKeys.appointable(localGovernmentId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<AppointableCandidate[]>(
        '/administration/appointments/appointable',
        { params: { localGovernmentId } },
      );
      return data;
    },
    enabled: Boolean(localGovernmentId),
  });
}

export function useCreateAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateAppointmentDto) => {
      const { data } = await api.post<AppointmentSummary>('/administration/appointments', dto);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gov-admin'] }),
  });
}

export function useApproveAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, localGovernmentId }: { id: string; localGovernmentId: string }) => {
      const { data } = await api.post<AppointmentSummary>(
        `/administration/appointments/${id}/approve`,
        { localGovernmentId },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gov-admin'] }),
  });
}

export function useRejectAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const { data } = await api.post<AppointmentSummary>(
        `/administration/appointments/${id}/reject`,
        { reason },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gov-admin'] }),
  });
}

export function useRevocationPlan(appointmentId: string | null, localGovernmentId: string) {
  return useQuery({
    queryKey: ['gov-admin', 'revocation-plan', appointmentId ?? ''],
    queryFn: async () => {
      const { data } = await api.get<RevocationPlan>(
        `/administration/appointments/${appointmentId}/revocation-plan`,
        { params: { localGovernmentId } },
      );
      return data;
    },
    enabled: Boolean(appointmentId),
  });
}

export function useRevokeAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      reason,
      overrides,
    }: {
      id: string;
      reason?: string;
      overrides?: Array<{ fileId: string; successorUserId: string }>;
    }) => {
      const { data } = await api.post(`/administration/appointments/${id}/revoke`, {
        reason,
        overrides,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gov-admin'] }),
  });
}

// ── Municipal seats ────────────────────────────────────────────────────────

/**
 * Everyone at this municipality who could be granted signing authority.
 *
 * Not `useGovernmentMembers`: an officer holds an appointment, not a seat, so at
 * a municipality served by a contractor the members list is a clerk and a
 * solicitor — and the grant dialog looked broken to the one person most likely
 * to open it.
 */
export function useSigningCandidates(localGovernmentId: string | null | undefined) {
  return useQuery({
    queryKey: ['gov-admin', 'signing-candidates', localGovernmentId ?? ''],
    queryFn: async () => {
      const { data } = await api.get<SigningCandidate[]>(
        `/administration/local-governments/${localGovernmentId}/signing-candidates`,
      );
      return data;
    },
    enabled: Boolean(localGovernmentId),
  });
}

export function useGovernmentMembers(localGovernmentId: string | null | undefined) {
  return useQuery({
    queryKey: adminKeys.members(localGovernmentId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<GovernmentMemberSummary[]>(
        `/administration/local-governments/${localGovernmentId}/members`,
      );
      return data;
    },
    enabled: Boolean(localGovernmentId),
  });
}

export function useAddGovernmentMember(localGovernmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateGovernmentMemberDto) => {
      const { data } = await api.post<GovernmentMemberSummary>(
        `/administration/local-governments/${localGovernmentId}/members`,
        dto,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.members(localGovernmentId) }),
  });
}

export function useRemoveGovernmentMember(localGovernmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/administration/local-governments/${localGovernmentId}/members/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.members(localGovernmentId) }),
  });
}

// ── Signing authority ──────────────────────────────────────────────────────

export function useSigningAuthority(localGovernmentId: string | null | undefined) {
  return useQuery({
    queryKey: adminKeys.signing(localGovernmentId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<SigningAuthorityRow[]>(
        `/administration/local-governments/${localGovernmentId}/signing-authority`,
      );
      return data;
    },
    enabled: Boolean(localGovernmentId),
  });
}

export function useGrantSigningAuthority(localGovernmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: GrantSigningAuthorityDto) => {
      const { data } = await api.post<SigningAuthorityRow[]>(
        `/administration/local-governments/${localGovernmentId}/signing-authority`,
        dto,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.signing(localGovernmentId) }),
  });
}

export function useRevokeSigningAuthority(localGovernmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, documentKind }: { userId: string; documentKind: string }) => {
      await api.delete(
        `/administration/local-governments/${localGovernmentId}/signing-authority/${userId}/${documentKind}`,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.signing(localGovernmentId) }),
  });
}

// ── File teams ─────────────────────────────────────────────────────────────

export function useFileTeam(fileId: string | null | undefined) {
  return useQuery({
    queryKey: adminKeys.fileTeam(fileId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<FileTeamMember[]>(`/files/${fileId}/team`);
      return data;
    },
    enabled: Boolean(fileId),
  });
}

export function useAddFileTeamMember(fileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: AddFileTeamMemberDto) => {
      const { data } = await api.post<FileTeamMember[]>(`/files/${fileId}/team`, dto);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.fileTeam(fileId) }),
  });
}

export function useRemoveFileTeamMember(fileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      await api.delete(`/files/${fileId}/team/${assignmentId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.fileTeam(fileId) }),
  });
}

// ── A contractor's local-government contacts ───────────────────────────────

export function useLgContacts(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ['gov-admin', 'lg-contacts', companyId ?? ''],
    queryFn: async () =>
      (
        await api.get<LgContactsSummary[]>(
          `/administration/companies/${companyId}/local-government-contacts`,
        )
      ).data,
    enabled: Boolean(companyId),
  });
}

export function useAddLgContact(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateLgContactDto) =>
      (
        await api.post<GovernmentMemberSummary>(
          `/administration/companies/${companyId}/local-government-contacts`,
          dto,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gov-admin'] }),
  });
}

export function useRemoveLgContact(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (membershipId: string) => {
      await api.delete(
        `/administration/companies/${companyId}/local-government-contacts/${membershipId}`,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gov-admin'] }),
  });
}
